/**
 * The two package-bound save destinations: push the draft's content back
 * into the package's sets ("update for everyone"), or copy the draft into a
 * brand-new package (both sets + link, wizard-style sequential creates) and
 * return its id so the config can repoint.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { FlowOptimizerConfig } from '@trafficpulse/shared';

import { libraryKeys } from '../../api/keys';
import { createPackage, getPackage } from '../../api/client/packages-api';
import { createSet, updateSet } from '../../api/client/sets-api';

interface PackageSaveArgs {
  packageId: string;
  draft: FlowOptimizerConfig;
}

interface ForkArgs extends PackageSaveArgs {
  name: string;
}

export function usePackageSave() {
  const queryClient = useQueryClient();
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: libraryKeys.all });

  const updateEveryone = useMutation({
    mutationFn: async ({ packageId, draft }: PackageSaveArgs) => {
      const pkg = await getPackage(packageId);
      await updateSet('formula', pkg.formulaSetId, { formula: draft.formula });
      await updateSet('rule', pkg.ruleSetId, {
        excludeRules: draft.excludeRules,
        capacityRules: draft.capacityRules ?? [],
      });
      return pkg;
    },
    onSuccess: invalidate,
  });

  const fork = useMutation({
    mutationFn: async ({ packageId, draft, name }: ForkArgs) => {
      const source = await getPackage(packageId);
      const formulaSet = await createSet('formula', {
        name: `${name} — formulas`,
        formula: draft.formula,
      });
      const ruleSet = await createSet('rule', {
        name: `${name} — rules`,
        excludeRules: draft.excludeRules,
        capacityRules: draft.capacityRules ?? [],
      });
      return createPackage({
        name,
        color: source.color ?? undefined,
        formulaSetId: formulaSet.id,
        ruleSetId: ruleSet.id,
      });
    },
    onSuccess: invalidate,
  });

  return { updateEveryone, fork };
}
